import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { CollisionPriority } from '@dnd-kit/abstract';
import { useSortable } from '@dnd-kit/react/sortable';

import { FloatingBlockToolbarPortal } from '@/components/editor/toolbar/floating-block-toolbar-portal';
import { useStickyHover } from '@/hooks/use-sticky-hover';
import { isFullBleedImageBlock } from '@/lib/blocks/layout/block-stack-layout';
import { cn } from '@/lib/utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import {
  BLOCK_TYPE,
  type BlockType,
  getBlockUiKey,
  isVisualMediaBlockType,
  type PageBlock,
} from '@/types/block';
import { BlockRenderer } from '../renderers/block-renderer';
import { BlockInlineTextEditor } from './block-inline-text-editor';
import {
  useBlockSelectionActions,
  useBlockSelectionBindings,
  useClearActiveForSection,
  useInlineEditBlockId,
  useIsBlockActive,
  useIsBlockHovered,
  useIsBlockToolbarRowVisible,
  useIsOverlayFocused,
} from './block-selection';
import { BlockShell } from './block-shell';
import { createDynamicCollisionDetector } from './dnd/collision/dynamic-detector';
import { type DropIndicator, type DropParentDirection, isDirectionalEdge } from './dnd/core';
import { getPlacementIndicatorClassName, getPreviewGapClassName } from './dnd/drag-visuals';
import { GapZone } from './dnd/drop-targets/gap-zone';
import {
  type EditorContentAlign,
  getEditorBlockRootClassName,
  getEditorContentAnchorClassName,
  resolveEditorBlockVisualWidth,
} from './editor-content-bounds';
import { BlockDiffOverlay } from './proposal-diff/block-diff-overlay';
import { ReviewIndexSettingsDialog } from './review-index-settings-dialog';
import { BlockActionToolbar } from './toolbar/block-action-toolbar';
import { resolveLeafToolbarPolicy } from './toolbar/toolbar-policy';
import { useLeafBlockEditOverlays } from './toolbar/use-leaf-block-edit-overlays';

interface SortableLeafBlockProps {
  sectionId: string;
  editorSectionId: string;
  block: PageBlock;
  parentStableId: string | null;
  parentBlockType?: BlockType | null;
  parentDirection: DropParentDirection;
  dropIndicator: DropIndicator | null;
  contentAlign: EditorContentAlign;
  isDragSessionActive?: boolean;
  disabled?: boolean;
  dndDisabled?: boolean;
  stretchToParent?: boolean;
  layoutClassName?: string;
  structuralEditableBlockIds?: ReadonlySet<string>;
  protectedBlockIds?: ReadonlySet<string>;
}

const LeafBlockContent = memo(function LeafBlockContent({
  block,
  stretchToParent,
  interactive,
}: {
  block: PageBlock;
  stretchToParent: boolean;
  interactive: boolean;
}) {
  return (
    <div className={stretchToParent ? 'h-full w-full' : undefined}>
      <BlockRenderer block={block} interactive={interactive} />
    </div>
  );
});

export function SortableLeafBlock({
  sectionId,
  editorSectionId,
  block,
  parentStableId,
  parentBlockType = null,
  parentDirection,
  dropIndicator,
  contentAlign,
  isDragSessionActive = false,
  disabled,
  dndDisabled,
  stretchToParent = false,
  layoutClassName,
  structuralEditableBlockIds,
  protectedBlockIds,
}: SortableLeafBlockProps) {
  const blockId = block.id;
  const blockUiKey = getBlockUiKey(block);
  const isTextBlock =
    block.blockType === 'text' &&
    block.props?.slot !== 'practice_areas_value' &&
    block.props?.slot !== 'topics_value';
  const isVisualMediaBlock = isVisualMediaBlockType(block.blockType);
  const blockRootRef = useRef<HTMLDivElement | null>(null);
  const primaryEditButtonRef = useRef<HTMLButtonElement | null>(null);
  const toolbarContainerRef = useRef<HTMLDivElement | null>(null);
  const setToolbarContainerEl = useCallback((el: HTMLDivElement | null) => {
    toolbarContainerRef.current = el;
  }, []);
  const collisionDetector = useMemo(() => createDynamicCollisionDetector(), []);

  const deleteBlock = usePageEditorStore((state) => state.deleteBlock);
  const { setActiveBlock, setInlineEdit, beginInlineEdit } = useBlockSelectionActions();
  const clearActiveForSection = useClearActiveForSection(editorSectionId);
  const isOutsideStructuralRegion = Boolean(
    structuralEditableBlockIds && !structuralEditableBlockIds.has(blockUiKey),
  );
  const isProtectedBlock = protectedBlockIds?.has(blockUiKey) ?? false;
  const isParentProtected = Boolean(parentStableId && protectedBlockIds?.has(parentStableId));
  const isDndDisabled = Boolean(
    disabled || dndDisabled || isOutsideStructuralRegion || isProtectedBlock,
  );

  const { ref, isDragging } = useSortable({
    id: blockUiKey,
    data: { type: 'block', parentId: parentStableId, isParentBlock: false },
    disabled: isDndDisabled,
    index: block.position,
    collisionPriority: CollisionPriority.Low,
    collisionDetector,
  });
  const { onPrimaryEdit, overlayNode } = useLeafBlockEditOverlays(
    sectionId,
    editorSectionId,
    block,
    toolbarContainerRef,
    parentBlockType,
  );
  const blockSelectionBindings = useBlockSelectionBindings(editorSectionId, blockUiKey);
  const isActive = useIsBlockActive(editorSectionId, blockUiKey);
  const isHovered = useIsBlockHovered(editorSectionId, blockUiKey);
  const isToolbarRowVisibleBySelection = useIsBlockToolbarRowVisible(editorSectionId, blockUiKey);
  const inlineEditBlockId = useInlineEditBlockId(editorSectionId);
  const isInlineEditMode = inlineEditBlockId === blockUiKey;
  const toolbarPolicy = resolveLeafToolbarPolicy({
    parentStableId,
    parentBlockType,
    blockType: block.blockType,
    lockedSection: isOutsideStructuralRegion,
  });
  const canSelectParent = toolbarPolicy.canSelectParent && !isParentProtected;

  const isOverlayFocused = useIsOverlayFocused();
  const {
    isHovered: stickyHoverInSubtree,
    cancelHide: cancelCollectionHoverHide,
    scheduleHide: scheduleCollectionHoverHide,
  } = useStickyHover(250);
  useEffect(() => {
    if (!toolbarPolicy.showsToolbarOnHover) return;
    if (isHovered) {
      cancelCollectionHoverHide();
    } else if (stickyHoverInSubtree) {
      scheduleCollectionHoverHide();
    }
  }, [
    toolbarPolicy.showsToolbarOnHover,
    isHovered,
    stickyHoverInSubtree,
    cancelCollectionHoverHide,
    scheduleCollectionHoverHide,
  ]);

  const handleExitInlineEdit = useCallback(() => {
    setInlineEdit(editorSectionId, null);
  }, [editorSectionId, setInlineEdit]);

  const handlePrimaryEdit = useCallback(() => {
    if (isTextBlock) {
      beginInlineEdit(editorSectionId, blockUiKey, { suppressPageSectionChrome: true });
      return;
    }
    onPrimaryEdit?.();
  }, [beginInlineEdit, blockUiKey, editorSectionId, isTextBlock, onPrimaryEdit]);

  const handleSelectParent = useCallback(() => {
    if (!parentStableId) {
      return;
    }
    setActiveBlock(editorSectionId, parentStableId, { syncHover: true });
  }, [editorSectionId, parentStableId, setActiveBlock]);

  const handleDelete = useCallback(() => {
    if (isActive) {
      if (parentStableId) {
        setActiveBlock(editorSectionId, parentStableId, { syncHover: true });
      } else {
        clearActiveForSection();
      }
    }
    if (inlineEditBlockId === blockUiKey) {
      setInlineEdit(editorSectionId, null);
    }
    deleteBlock(sectionId, blockId, {
      preserveEmptyParentIds: protectedBlockIds,
    });
  }, [
    blockId,
    blockUiKey,
    clearActiveForSection,
    deleteBlock,
    editorSectionId,
    inlineEditBlockId,
    isActive,
    parentStableId,
    protectedBlockIds,
    sectionId,
    setActiveBlock,
    setInlineEdit,
  ]);

  const isEditing = isTextBlock && isActive && !isDragging && isInlineEditMode;
  const isInlineEditing = isInlineEditMode && !isDragging;
  const isDropHover = dropIndicator?.targetId === blockUiKey;
  const dropEdge = isDropHover ? dropIndicator?.edge : null;
  const previewGapClass =
    isDragSessionActive && isDropHover && isDirectionalEdge(dropEdge)
      ? getPreviewGapClassName(dropEdge)
      : null;
  const placementPreviewNode =
    isDragSessionActive && isDropHover && isDirectionalEdge(dropEdge) ? (
      <div className={getPlacementIndicatorClassName(dropEdge)} />
    ) : null;
  const isBlockLocked = Boolean(block.props?.locked);
  const showHoverContextToolbar =
    toolbarPolicy.showsToolbarOnHover && !isOverlayFocused && (isHovered || stickyHoverInSubtree);
  const toolbarRowVisible =
    (isToolbarRowVisibleBySelection || showHoverContextToolbar) &&
    !isDragging &&
    !isDragSessionActive &&
    !isBlockLocked;
  const showBlockActionToolbar = toolbarRowVisible && !(isTextBlock && isInlineEditing);
  const visualWidth = resolveEditorBlockVisualWidth(block);
  const isContentWidthBlock = visualWidth === 'content';
  const blockRootClassName = getEditorBlockRootClassName(block);
  const contentAnchorClassName = getEditorContentAnchorClassName({
    contentAlign,
    visualWidth,
    stretchToParent,
  });

  const isFullBleedImage = isFullBleedImageBlock(block);

  const openSettingsNode =
    block.blockType === BLOCK_TYPE.REVIEW_INDEX ? (
      <ReviewIndexSettingsDialog
        sectionId={sectionId}
        block={block}
        isVisible
        disabled={disabled}
        onOpenChange={(open) => {
          if (open) setActiveBlock(editorSectionId, blockUiKey, { syncHover: true });
        }}
      />
    ) : undefined;

  useEffect(() => {
    if (inlineEditBlockId === blockUiKey && !isActive) {
      setInlineEdit(editorSectionId, null);
    }
  }, [blockUiKey, editorSectionId, inlineEditBlockId, isActive, setInlineEdit]);

  return (
    <div
      ref={blockRootRef}
      className={cn(
        'relative',
        blockRootClassName,
        stretchToParent && 'h-full min-h-0',
        layoutClassName,
      )}
      data-block-selection-root={blockUiKey}
      {...blockSelectionBindings}
    >
      {toolbarRowVisible ? (
        <FloatingBlockToolbarPortal anchorRef={blockRootRef}>
          <div
            ref={setToolbarContainerEl}
            className="pointer-events-auto flex items-center"
            onMouseEnter={toolbarPolicy.showsToolbarOnHover ? cancelCollectionHoverHide : undefined}
            onMouseLeave={
              toolbarPolicy.showsToolbarOnHover ? scheduleCollectionHoverHide : undefined
            }
          >
            {showBlockActionToolbar ? (
              <BlockActionToolbar
                block={block}
                disabled={disabled}
                onPrimaryEdit={toolbarPolicy.canPrimaryEdit ? handlePrimaryEdit : undefined}
                primaryEditRef={primaryEditButtonRef}
                onSelectParent={canSelectParent ? handleSelectParent : undefined}
                onOpenSettings={openSettingsNode}
                onDelete={toolbarPolicy.canDelete && !isProtectedBlock ? handleDelete : undefined}
              />
            ) : null}
          </div>
        </FloatingBlockToolbarPortal>
      ) : null}
      {overlayNode}
      <BlockShell
        isParentLike={false}
        isSelected={isActive}
        isHovered={isHovered}
        isDropHover={isDropHover}
        isDragging={isDragging}
        className={cn(
          stretchToParent ? 'h-full w-full' : isContentWidthBlock ? 'w-auto max-w-full' : 'w-full',
          previewGapClass,
          isDragSessionActive && 'transition-[margin] duration-150 ease-out',
          isTextBlock && isActive && 'bg-editor-primary/3',
        )}
        fullBleed={isFullBleedImage}
      >
        <div
          ref={ref}
          className={cn(
            'group relative',
            isContentWidthBlock ? 'w-auto max-w-full' : 'w-full',
            isDragging &&
              cn(
                'scale-[0.95] transition-[opacity,transform] duration-150',
                isVisualMediaBlock ? 'opacity-40' : 'opacity-70',
              ),
            stretchToParent && 'h-full',
            !isInlineEditing && !isDndDisabled && 'cursor-pointer active:cursor-grabbing',
          )}
          onDoubleClick={
            isTextBlock || isDragging || isDndDisabled
              ? undefined
              : (event) => {
                  if (!onPrimaryEdit) return;
                  event.stopPropagation();
                  handlePrimaryEdit();
                }
          }
        >
          <GapZone
            blockId={blockUiKey}
            parentId={parentStableId}
            parentDirection={parentDirection}
            disabled={!isDragSessionActive || isDragging || isDndDisabled || isInlineEditMode}
          />
          {placementPreviewNode}
          <div className={contentAnchorClassName}>
            {isTextBlock ? (
              <BlockInlineTextEditor
                sectionId={sectionId}
                block={block}
                isEditing={isEditing}
                dragDisabled={isDndDisabled}
                onRequestEdit={isBlockLocked ? undefined : handlePrimaryEdit}
                onExitEditing={handleExitInlineEdit}
                toolbarAnchorRef={blockRootRef}
              />
            ) : (
              <BlockDiffOverlay block={block}>
                <LeafBlockContent
                  block={block}
                  stretchToParent={stretchToParent}
                  interactive={false}
                />
              </BlockDiffOverlay>
            )}
          </div>
        </div>
      </BlockShell>
    </div>
  );
}
